import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext, useMemo } from 'react';
import ReactRouterPropTypes from 'react-router-prop-types';

import { useTable, useSortBy, usePagination, useFilters } from 'react-table';
import { useDebouncedCallback } from 'use-debounce';

import axios from "axios";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

import { acquire } from '../util/Workbook';
import { READ, Om7Sense as p } from '../Om7Sense';
import { ReportingPageTabs } from './ReportingPageTabs.js';
import { useDateFrame } from "./useDateFrame";
import { TranslationContext } from "../hooks/useTranslationContext";
import { SearchableTable } from "../util/tables/SearchableTable";
import { Pagination } from "../util/tables/Pagination";
import { fullDateTimeFormat as dateTimeFormatOptions, fullDateFormat as dateFormatOptions } from '../util/dateTime';
import { ApiConnectionContext } from "../hooks/useApiConnectionContext";
import { useLocation } from "react-router-dom-v5-compat";

const STATES = ["ANY", "NOT_STARTED", "SUCCESS", "FAILED_DEVICE_NOT_FOUND", "FAILED_DEVICE_NOT_READY", "IGNORED_DEVICE_BUSY", "FAILED_OTHER"];

const xlsDateFormat = 'DD.MM.YYYY HH:mm:ss'; // navigator.language.indexOf('de') === 0)

function formatDate(date, options = dateTimeFormatOptions) {
    return Intl.DateTimeFormat(navigator.language, options).format(date).replace(/ /g, "\u00a0");
}

function Table({ columns, serverFilter, title, sensorId }) {
    const t = useContext(TranslationContext);
    const { startDate, endDate, printButton, timeFrameButton, calendar } = useDateFrame(title);
    // Use the state and functions returned from useTable to build your UI
    const [data, setData] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page, // Instead of using 'rows', we'll use page,
        // which has only the rows for the active page
        pageOptions,
    
        // The rest of these things are super handy, too ;)
        canPreviousPage,
        canNextPage,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state,
    } = useTable({
        columns,
        data,
        pageCount: pageCount,
        disableMultiSort: true,
        manualPagination: true,
        manualSortBy: true,
        manualFilters: true,
        autoResetPage: true,
        autoResetSortBy: false,
        initialState: {
            pageSize: 20,
            autoResetPage: true,
            sortBy: [{id: 'created', desc: true}],
        }
    }, useFilters, useSortBy, usePagination);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const fetchIdRef = React.useRef(0);

    const { pageSize, sortBy, pageIndex } = state;

    useEffect(() => {
        const url = '/apiv2/backupLog';
        const args = Object.assign({}, serverFilter);
        if (startDate) {
            args.startDate = dayjs(startDate).format('YYYY-MM-DD');
        }
        if (endDate) {
            const endDateObj = dayjs(endDate) || dayjs();
            args.endDate = endDateObj.format('YYYY-MM-DD');
        }
        if (sortBy.length) {
            args['orderBy'] = sortBy[0].id;
            args['orderAsc'] = !sortBy[0].desc;
        }
        if (sensorId) {
            args.sensorId = sensorId;
        }
        if (args.state === 'ANY') {
            delete(args.state);
        }
        args.page = pageIndex;
        args.pageSize = pageSize;
        setLoading(true);
        axios.get(url, { params: args }).then(response => {
            const backups = [];
            for (let b of response.data.items) {
                b.created = new Date(b.created);

                backups.push(b);
            }
            setPageCount(response.data.count);
            setData(backups);
            setLoading(false);
        }, error => {
            setError(error);
            setLoading(false);
        });
    }, [startDate, endDate, serverFilter.serial, serverFilter.type, serverFilter.firmware, serverFilter.vendor, serverFilter.state, serverFilter.name, sortBy, pageSize, pageIndex]);
    const loadingProps = loading ? { className: 'loading' } : {};

    const actualTable =
        error ? (
            <p>{t("reporting.error_loading")}</p>
        ) : (
            <React.Fragment>
                <SearchableTable tableProps={getTableProps(loadingProps)}
                                 state={ state } rows={ rows } columns={columns} headerGroups={headerGroups}
                                 prepareRow={ prepareRow } getTableBodyProps={ getTableBodyProps } page={ page }
                                 loading={ loading } emptyText={ t("reporting_backups.no_data") } />
                <Pagination page={ page } pageCount={ pageCount } pageOptions={ pageOptions }
                            canPreviousPage={ canPreviousPage } canNextPage={ canNextPage } gotoPage={ gotoPage }
                            previousPage={ previousPage } nextPage={ nextPage } setPageSize={ setPageSize }
                            pageSize={ state.pageSize } pageIndex={ state.pageIndex } />

            </React.Fragment>
        );

    const fullTitle = title + " - " + formatDate(startDate, dateFormatOptions) + " - " + formatDate(endDate, dateFormatOptions);
  
    // Render the UI for your table
    return (
        <React.Fragment>
            <div className="printnavi columns">
                { printButton }
                <button className="btn" onClick={() => exportExcel(data, t, columns, fullTitle, title)}>{t('reporting.button_excel_download')}</button>
                { timeFrameButton }
                { calendar}
            </div>
            <h3>{ fullTitle }</h3>
            <div className="overview classic scrollx">
                { actualTable }
            </div>
        </React.Fragment>
    );
}

const exportExcel = (rows, t, columns, title, sheetTitle) => {
    // Create an empty workbook
    acquire().then(Workbook => {

        let wb = new Workbook();
        // Make column 0 be the width of the date format
        const colOptions = [
            { wch: xlsDateFormat.length},
        ];
        let sheetOptions = {'!merges':[], '!cols': colOptions};
        let sheet = wb.add(sheetTitle || title, [], sheetOptions);

        sheet[0] = [];
        // Add first row with headers
        for (const columnIdx in columns) {
            sheet[0][columnIdx] = columns[columnIdx].Header;
        }
        let rowCount = 1;
        for (const row of rows) {
            sheet[rowCount] = [];
            for (const columnIdx in columns) {
                let cellValue;
                const column = columns[columnIdx];
                // Check if there is a special XLS accessor
                if (column.noXls) {
                    continue;
                }
                if (column.toXls) {
                    cellValue = column.toXls(row);
                } else if (typeof column.accessor === 'function') {
                    cellValue = column.accessor(row);
                } else if (column.accessor) {
                    cellValue = row[column.accessor];
                } else {
                    continue;
                }
                sheet[rowCount][columnIdx] = cellValue;
            }
            rowCount++;
        }
        wb.save(title + ".xlsx", xlsDateFormat);
    });
};

const BackupReportPage = (props) => {
    const t = useContext(TranslationContext);
    const { p, loggedInAs } = useContext(ApiConnectionContext);
    const permission = useMemo(() => p.checkPermission(READ, "/apiv2/backupLog"), [ p, loggedInAs ]);
    const [ serverFilter, setServerFilter ] = useState({});
    const location = useLocation();
    const debouncedServerFilter = useDebouncedCallback(
        // function
        (value) => {
            setServerFilter(value);
        },
        // delay in ms
        500
    );

    // Define a default UI for filtering
    function DefaultColumnFilter({
        column: { filterValue, preFilteredRows, setFilter, serverFilterAttr },
    }) {
        const count = preFilteredRows.length;

        return (
            <input
                value={filterValue || ''}
                onChange={e => {
                    setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
                    const update = {};
                    update[serverFilterAttr] = e.target.value || undefined;
                    debouncedServerFilter.callback(update);
                }}
                placeholder={t("general.search__records", { count })}
            />
        );
    }

    // State selector drop-down
    function StateSelectorFilter({
        column: { filterValue, setFilter, serverFilterAttr },
    }) {

        return (
            <select
                value={filterValue || ''}
                onChange={e => {
                    setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
                    const update = {};
                    update[serverFilterAttr] = e.target.value || undefined;
                    debouncedServerFilter.callback(update);
                }}
            >
                {
                    STATES.map((key) => 
                        <option key={key} value={key}>
                            {t("reporting_backups.state." + key)}
                        </option>)
                }
            </select>
        );
    }

    const columns = React.useMemo(
        () => [
            {
                Header: t("notifications.title_date"),
                id: 'created',
                accessor: row => formatDate(row.created),
                disableFilters: true,
                toXls: row => row.created
            }, {
                id: 'state',
                Header: t("general.state"),
                accessor: row => t("reporting_backups.state." + row.state),
                serverFilterAttr: 'state',
                Filter: StateSelectorFilter,
            }, props.sensorId ? undefined : {
                id: 'name',
                Header: t("general.pdu_name"),
                accessor: 'name',
                serverFilterAttr: 'name',
                Filter: DefaultColumnFilter,
            }, {
                id: 'model',
                Header: t("topic_titles_info.Model"),
                accessor: 'model',
                serverFilterAttr: 'model',
                Filter: DefaultColumnFilter,
            }, {
                id: 'vendor',
                Header: t("device.vendor"),
                accessor: 'vendor',
                Filter: DefaultColumnFilter,
                serverFilterAttr: 'vendor',
                disableFilters: false,
            }, {
                id: 'serial',
                Header: t("topic_titles_info.SerialNumber"),
                accessor: 'serial',
                Filter: DefaultColumnFilter,
                serverFilterAttr: 'serial',
                disableFilters: false,
            }, {
                id: 'firmware',
                Header: t("topic_titles_info.FirmwareVersion"),
                accessor: 'firmwareVersion',
                serverFilterAttr: 'firmware',
                Filter: DefaultColumnFilter,
                disableFilters: false,
            }, {
                id: 'actions',
                Header: t("settings.header_actions"),
                accessor: row =>
                    row.state === 'SUCCESS' && 
                        <a target='_blank' href={"/apiv2/backup/dl/" + row.id}>Download</a>,
                disableFilters: true,
                disableSortBy: true,
                noXls: true,
            }
        ].filter(item => item),
        []
    );

    const innerEl = !permission ? (
        <div className="empty">
            <div className="empty-icon">
                <i className="icon icon-people"></i>
            </div>
            <p className="empty-title h5">{ t("reporting.no_permission") }</p>
        </div>
    ) : ( 
        <Table {...props} columns={columns} serverFilter={serverFilter} />
    );

    if (!props.sensorId) {
        return (
            <React.Fragment>
                <ReportingPageTabs location={location} />
                { innerEl }
            </React.Fragment>
        );
    } else {
        return innerEl;
    }
    
};

BackupReportPage.propTypes = {
    deviceType: PropTypes.string,
    vendor: PropTypes.number,
    serial: PropTypes.bool,
    firmware: PropTypes.object,
    startSlice: PropTypes.number,
    sensorId: PropTypes.string,
    title: PropTypes.string,
};


export { BackupReportPage };